/**
 * draftModel.js — the data model and migration for the merged "Draft" tool.
 *
 * Draft replaces the separate Brainstorm and Design tools, but its
 * canonical storage stays the EXISTING `data.design` slice, unchanged in
 * shape. That means the whole Builder pipeline (generateSlides, overrides,
 * inserted slides) keeps reading the same data with zero changes.
 *
 * A Draft "section" === a design chunk:
 *   { id, title, color, subtopics:[{ id, time, title, dictation, sourceBitId? }],
 *     discussion:{ open, type, prompt, time }, transition, sourceChunkId? }
 *
 * Two additive, Builder-ignored fields live alongside on the design slice:
 *   capture: [{ id, text }]   — the flat brain-dump buffer (Capture phase)
 *   phase:   'capture'|'shape'
 *
 * MIGRATION (the careful part)
 * ----------------------------
 * Brainstorm and Design are NOT parallel content — Design is the refined
 * downstream of Brainstorm and records provenance (sourceChunkId /
 * sourceBitId). So a brainstorm bit's text already lives in Design as a
 * subtopic title. We therefore use Design as the base and fold in only
 * brainstorm content that isn't already represented, using the app's own
 * additive merge (syncFromBrainstorm) — the exact logic that has been
 * running behind Design's "Pull new bits from Brainstorm" button.
 *
 * Guarantees:
 *   - Existing design chunk IDs are PRESERVED (Builder overrides and
 *     inserted-slide anchors are keyed to `section-${id}` / `content-${id}`,
 *     so regenerating IDs would orphan users' manual edits — we never do).
 *   - The brainstorm slice is never deleted; it stays as a fallback source.
 *   - Idempotent: re-running on already-migrated data is a no-op and
 *     returns the SAME object reference (so it never triggers a spurious save).
 *   - Old {time, body} subtopics are upgraded to {time, title, dictation}.
 */

// ── shared id generator (numeric, monotonic within a session) ──
// Seeded from Date.now() so a fresh session's ids are always larger than
// any id minted in a previous session — no collisions with saved data.
let _id = Date.now();
export const genId = () => ++_id;

// ─────────────────────────────────────────────────────────────────────
// PORTED VERBATIM from the Design tool (proven in production).
// Kept byte-for-byte equivalent so migration behaviour matches what the
// old Design first-visit import + "Pull" button already did.
// ─────────────────────────────────────────────────────────────────────

const MAX_SUBTOPICS = 6;

export function blankSubtopic() {
  return { time: '', title: '', dictation: '' };
}

export function blankChunk(color = 1) {
  return {
    id: genId(),
    title: '',
    color,
    subtopics: [blankSubtopic(), blankSubtopic(), blankSubtopic(), blankSubtopic()],
    discussion: { open: false, type: '', prompt: '', time: '' },
    transition: '',
  };
}

function migrateSubtopic(s) {
  if (!s || typeof s !== 'object') return blankSubtopic();
  const hasNewShape = 'title' in s || 'dictation' in s;
  if (hasNewShape) {
    return {
      ...s,
      time: s.time ?? '',
      title: s.title ?? '',
      dictation: s.dictation ?? s.body ?? '',
    };
  }
  return {
    ...s,
    time: s.time ?? '',
    title: '',
    dictation: s.body ?? '',
  };
}

function migrateChunk(c) {
  return {
    ...c,
    subtopics: (c.subtopics ?? []).map(migrateSubtopic),
  };
}

function chunkNeedsMigration(c) {
  return (c.subtopics ?? []).some((s) => !('title' in s) || !('dictation' in s));
}

export function isChunkBlank(c) {
  if (c.title && c.title.trim()) return false;
  if (
    c.subtopics?.some(
      (s) =>
        (s.title && s.title.trim()) ||
        (s.dictation && s.dictation.trim()) ||
        (s.time && String(s.time).trim())
    )
  )
    return false;
  if (c.discussion?.type || c.discussion?.prompt || c.discussion?.time) return false;
  if (c.transition && c.transition.trim()) return false;
  return true;
}

function brainstormChunkToDesignChunk(bc) {
  const fresh = blankChunk(bc.color);
  fresh.title = bc.title || '';
  fresh.sourceChunkId = bc.id;
  const bitsWithIds = (bc.bits ?? [])
    .map((b) => ({ id: b?.id, text: (b?.text ?? '').trim() }))
    .filter((b) => b.text);

  if (bitsWithIds.length === 0) return fresh;

  const used = bitsWithIds.slice(0, MAX_SUBTOPICS);
  const extras = bitsWithIds.slice(MAX_SUBTOPICS);

  const subtopics = used.map((b) => ({
    time: '',
    title: b.text,
    dictation: '',
    sourceBitId: b.id,
  }));
  if (extras.length > 0) {
    subtopics[MAX_SUBTOPICS - 1].title += ' · ' + extras.map((b) => b.text).join(' · ');
  }
  fresh.subtopics = subtopics;
  return fresh;
}

function bitToPoint(bit) {
  return {
    time: '',
    title: (bit.text ?? '').trim(),
    dictation: '',
    sourceBitId: bit.id,
  };
}

function computeNewFromBrainstorm(bsChunks, designChunks) {
  let newBitCount = 0;
  let newChunkCount = 0;
  const designChunkSourceIds = new Set(
    designChunks.map((c) => c.sourceChunkId).filter(Boolean)
  );
  const designBitSourceIds = new Set();
  for (const dc of designChunks) {
    for (const s of dc.subtopics ?? []) {
      if (s.sourceBitId) designBitSourceIds.add(s.sourceBitId);
    }
  }

  for (const bc of bsChunks) {
    const bsBits = (bc.bits ?? []).filter((b) => (b?.text ?? '').trim());
    const bcHasContent = (bc.title && bc.title.trim()) || bsBits.length > 0;
    if (!bcHasContent) continue;

    if (!designChunkSourceIds.has(bc.id)) {
      newChunkCount += 1;
      newBitCount += bsBits.length;
    } else {
      for (const b of bsBits) {
        if (!designBitSourceIds.has(b.id)) newBitCount += 1;
      }
    }
  }

  return { newBitCount, newChunkCount };
}

function syncFromBrainstorm(bsChunks, designChunks) {
  const designBySourceId = new Map();
  for (const dc of designChunks) {
    if (dc.sourceChunkId) designBySourceId.set(dc.sourceChunkId, dc);
  }

  const result = designChunks.map((dc) => {
    if (!dc.sourceChunkId) return dc;
    const bc = bsChunks.find((c) => c.id === dc.sourceChunkId);
    if (!bc) return dc;

    const existingSourceBitIds = new Set(
      (dc.subtopics ?? []).map((s) => s.sourceBitId).filter(Boolean)
    );
    const newBits = (bc.bits ?? []).filter(
      (b) => (b?.text ?? '').trim() && !existingSourceBitIds.has(b.id)
    );
    if (newBits.length === 0) return dc;

    const room = Math.max(0, MAX_SUBTOPICS - (dc.subtopics?.length ?? 0));
    const fits = newBits.slice(0, room).map(bitToPoint);
    const overflow = newBits.slice(room);
    let nextSubtopics = [...(dc.subtopics ?? []), ...fits];
    if (overflow.length > 0 && nextSubtopics.length > 0) {
      const last = nextSubtopics[nextSubtopics.length - 1];
      const extras = overflow.map((b) => (b.text ?? '').trim()).filter(Boolean);
      nextSubtopics[nextSubtopics.length - 1] = {
        ...last,
        title: `${last.title} · ${extras.join(' · ')}`,
      };
    }
    return { ...dc, subtopics: nextSubtopics };
  });

  for (const bc of bsChunks) {
    if (designBySourceId.has(bc.id)) continue;
    const bcHasContent =
      (bc.title && bc.title.trim()) ||
      (bc.bits ?? []).some((b) => (b?.text ?? '').trim());
    if (!bcHasContent) continue;
    result.push(brainstormChunkToDesignChunk(bc));
  }

  return result;
}

// ─────────────────────────────────────────────────────────────────────
// NEW: orchestration, id-normalisation, phase selection
// ─────────────────────────────────────────────────────────────────────

export function brainstormHasContent(bsChunks) {
  return (bsChunks ?? []).some(
    (c) =>
      (c.title && c.title.trim()) ||
      (c.bits ?? []).some((b) => (b?.text ?? '').trim())
  );
}

export function isDesignEssentiallyEmpty(chunks) {
  return !chunks || chunks.length === 0 || chunks.every(isChunkBlank);
}

/**
 * Ensure every section + point carries a stable `id`. Points (subtopics)
 * had none in the Design schema (they were keyed by array index), but the
 * Draft Shape phase lets points be dragged between sections, which needs
 * stable ids. Additive only — Builder ignores the field. Returns a new
 * array only when something actually changed.
 */
export function ensureIds(chunks) {
  if (!Array.isArray(chunks)) return chunks;
  let changed = false;
  const next = chunks.map((c) => {
    let cChanged = false;
    const id = c.id != null ? c.id : (cChanged = true, genId());
    const subtopics = (c.subtopics ?? []).map((s) => {
      if (s.id != null) return s;
      cChanged = true;
      return { ...s, id: genId() };
    });
    if (!cChanged) return c;
    changed = true;
    return { ...c, id, subtopics };
  });
  return changed ? next : chunks;
}

/**
 * Does this plan's design slice need any migration work? Used to keep the
 * migration idempotent: if nothing needs doing we return the original
 * object reference so no spurious save is triggered on load.
 */
function designNeedsWork(data) {
  const design = data.design ?? {};
  const chunks = design.chunks ?? [];
  const bs = data.brainstorm?.chunks ?? [];

  if (chunks.some(chunkNeedsMigration)) return true;

  const essentiallyEmpty = isDesignEssentiallyEmpty(chunks);
  const bsHasContent = brainstormHasContent(bs);

  if (essentiallyEmpty && bsHasContent) return true;
  if (!essentiallyEmpty && bsHasContent) {
    const { newBitCount, newChunkCount } = computeNewFromBrainstorm(bs, chunks);
    if (newBitCount > 0 || newChunkCount > 0) return true;
  }

  // Points missing ids (older saves) → normalise.
  if (chunks.some((c) => (c.subtopics ?? []).some((s) => s.id == null))) {
    // Only counts as work if there are real chunks to normalise.
    if (!essentiallyEmpty || bsHasContent) return true;
  }

  // Duration fallback, only meaningful once a non-empty design exists.
  if ((design.duration ?? '') === '' && (data.pages?.total || data.logistics?.duration)) {
    if (!essentiallyEmpty || bsHasContent) return true;
  }

  return false;
}

/**
 * migratePlanData(data) — fold legacy Brainstorm + Design state into the
 * combined Draft (design) shape. Pure; returns a new object only when
 * something changed (otherwise the same reference).
 *
 * Order of operations mirrors the old Design first-visit effect:
 *   1. upgrade old {time, body} subtopics
 *   2. if design is essentially empty AND brainstorm has content → seed
 *      design from brainstorm
 *      else if brainstorm has content → additively sync new brainstorm
 *      content into existing design (dedup by sourceChunkId/sourceBitId)
 *   3. normalise point ids
 *   4. fill an empty duration target from pages.total / logistics.duration
 *
 * It never seeds blank placeholder sections (that was a fresh-tool-open
 * behaviour; the Draft Capture phase replaces it), and never deletes the
 * brainstorm slice.
 */
export function migratePlanData(data) {
  if (!data || typeof data !== 'object') return data;
  if (!designNeedsWork(data)) return data;

  const design = { ...(data.design ?? {}) };
  let chunks = (design.chunks ?? []).map((c) =>
    chunkNeedsMigration(c) ? migrateChunk(c) : c
  );

  const bs = data.brainstorm?.chunks ?? [];
  if (brainstormHasContent(bs)) {
    if (isDesignEssentiallyEmpty(chunks)) {
      chunks = bs.map(brainstormChunkToDesignChunk);
    } else {
      chunks = syncFromBrainstorm(bs, chunks);
    }
  }

  chunks = ensureIds(chunks);
  design.chunks = chunks;

  if ((design.duration ?? '') === '' && chunks.length > 0) {
    const fallback = data.pages?.total || data.logistics?.duration || '';
    if (fallback) design.duration = String(fallback);
  }

  return { ...data, design };
}

/**
 * Which phase should Draft open in? Respects a persisted choice, else
 * infers: anyone who already has section content lands in Shape; a fresh
 * plan starts in Capture.
 */
export function deriveInitialPhase(design) {
  const d = design ?? {};
  if (d.phase === 'capture' || d.phase === 'shape') return d.phase;
  const chunks = d.chunks ?? [];
  const hasSections = chunks.some((c) => !isChunkBlank(c));
  return hasSections ? 'shape' : 'capture';
}

// Exposed for the Draft UI / tests.
export const _internals = {
  brainstormChunkToDesignChunk,
  syncFromBrainstorm,
  computeNewFromBrainstorm,
  migrateChunk,
  chunkNeedsMigration,
  MAX_SUBTOPICS,
};
